import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbInputModule,
  NbSelectModule,
  NbToastrService,
} from '@nebular/theme';
import { NgxMaskDirective } from 'ngx-mask';
import { finalize, switchMap } from 'rxjs/operators';
import { Pessoa } from '../../../core/api/pessoa';
import { PessoaService } from '../../../core/api/pessoa.service';
import { PessoaDto } from '../../../core/api/pessoaDto';
import { PessoaTheosComparacaoDto } from '../../../core/api/pessoa-theos-comparacao.dto';
import { DialogAlertComponent } from '../../inscricao/inscricao.dialog.component';
import { mensagemDeErro } from '../../../utils/mensagem-erro';
import { validarCPF } from '../../../utils/validar-cpf';
import { MASCARAS } from '../../../utils/mascaras';
@Component({
  selector: 'app-pessoa-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbInputModule,
    NbButtonModule,
    NbCheckboxModule,
    NbSelectModule,
    NgxMaskDirective,
  ],
  templateUrl: './pessoa-form.component.html',
  styleUrls: ['./pessoa-form.component.scss'],
})
export class PessoaFormComponent implements OnInit {
  /**
   * `MSG-11` — formulário inválido marca **todos** os campos como tocados ao tentar
   * salvar, para a tela mostrar de uma vez tudo o que falta. Botão que não responde ao
   * clique é lido como sistema travado.
   */
  @ViewChild('formPessoa') protected formulario?: NgForm;

  private formularioValido(): boolean {
    if (this.formulario && this.formulario.invalid) {
      this.formulario.control.markAllAsTouched();
      this.dialogService.open(DialogAlertComponent, {
        context: {
          title: 'Faltam campos',
          message: 'Preencha os campos marcados com * antes de salvar. Eles estão destacados no formulário.',
        },
      });
      return false;
    }
    return true;
  }

  /** `FRM-10` — máscara vem de um lugar só. */
  protected readonly mascaras = MASCARAS;

  @Input() pessoa: Pessoa = new Pessoa();

  fotoPopupAberta = false;
  fotoPopupUrl: string | null = null;
  fotoPopupTitulo = '';
  apagandoFoto = false;

  theosComparacao: PessoaTheosComparacaoDto | null = null;
  carregandoComparacaoTheos = false;
  enviandoTheos = false;
  /** Após GET /pessoas/{id} — evita mensagem “sem Theos” antes de hidratar o cadastro. */
  pessoaDetalheCarregada = false;

  /** Exibição/edição da data de nascimento em dd/MM/yyyy (API usa LocalDate / ISO no JSON). */
  dataNascimentoBr = '';

  /**
   * Mantém o badge visível após “copiar do Theos” até uma nova resposta de {@link #carregarComparacaoTheos}
   * (antes o badge sumia porque o campo passava a ser igual ao Theos).
   */
  exibirBadgeTheosNome = false;
  exibirBadgeTheosCpf = false;
  exibirBadgeTheosSexo = false;
  exibirBadgeTheosData = false;
  exibirBadgeTheosEmail = false;

  constructor(
    private pessoaService: PessoaService,
    protected ref: NbDialogRef<PessoaFormComponent>,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  /** `FRM-07` — estado da conferência do documento. */
  cpfInvalido = false;
  verificandoCpf = false;
  avisoCpf = '';

  /**
   * `FRM-07` — documento válido dispara **primeiro** a checagem de duplicidade, e havendo
   * registro a tela oferece abrir o existente em vez de criar outro. Começar pelo nome é o
   * que produz o cadastro duplicado que só aparece na conciliação — e aqui o estrago passa
   * da catequese, porque pessoa duplicada vira duas pessoas para a integração com o Theos.
   */
  aoInformarCpf(): void {
    this.cpfInvalido = false;
    this.avisoCpf = '';

    const digitos = PessoaFormComponent.somenteDigitos(this.pessoa.cpf);
    if (!digitos) {
      return;
    }
    if (digitos.length !== 11 || !validarCPF(digitos)) {
      this.cpfInvalido = true;
      return;
    }
    // Em edição o registro já é o dono deste CPF: não há duplicidade a procurar.
    if (this.pessoa.id) {
      return;
    }
    this.conferirDuplicidade(digitos);
  }

  private conferirDuplicidade(cpf: string): void {
    this.verificandoCpf = true;
    this.pessoaService
      .buscaComFiltros(0, 5, undefined, cpf)
      .pipe(finalize(() => (this.verificandoCpf = false)))
      .subscribe({
        next: (pagina) => {
          const existente = (pagina.content ?? []).find(
            (p) => PessoaFormComponent.somenteDigitos(p.cpf) === cpf,
          );
          if (existente) {
            this.oferecerRegistroExistente(existente);
          }
        },
        // FRM-07: serviço indisponível não bloqueia o cadastro — avisa e libera.
        error: (err) => {
          this.avisoCpf = mensagemDeErro(
            err,
            'Não foi possível conferir se este CPF já está cadastrado. Confira antes de salvar.',
          );
        },
      });
  }

  private oferecerRegistroExistente(existente: Pessoa): void {
    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'CPF já cadastrado',
          message: `Este CPF já pertence a ${existente.nome ?? 'um cadastro existente'}. Em vez de criar outro registro, abra o que já existe.`,
          mostrarConfirmacao: true,
          msgConfirmacao: 'Abrir o cadastro existente',
        },
      })
      .onClose.subscribe((abrir) => {
        if (abrir) {
          this.aplicarPessoaDoServidor(existente);
        } else {
          this.avisoCpf = 'Este CPF já está cadastrado para outra pessoa.';
        }
      });
  }

  ngOnInit(): void {
    this.syncDataNascimentoBrFromPessoa();
    const id = this.pessoa.id?.trim();
    if (!id) {
      return;
    }
    this.pessoaService.buscarPorId(id).subscribe({
      next: (completa) => {
        this.aplicarPessoaDoServidor(completa);
        this.pessoaDetalheCarregada = true;
        if (this.codigoTheosNumerico(this.pessoa.codigoTheos)) {
          this.carregarComparacaoTheos();
        }
      },
      error: () => {
        this.pessoaDetalheCarregada = true;
        if (this.codigoTheosNumerico(this.pessoa.codigoTheos)) {
          this.carregarComparacaoTheos();
        }
      },
    });
  }

  /** Garante {@code codigoTheos} e demais campos vindos do GET /pessoas/{id} (o diálogo costuma receber objeto parcial). */
  private aplicarPessoaDoServidor(completa: Pessoa): void {
    Object.assign(this.pessoa, completa);
    if (this.pessoa.codigoTheos != null && typeof this.pessoa.codigoTheos === 'number') {
      this.pessoa.codigoTheos = String(this.pessoa.codigoTheos);
    }
    this.syncDataNascimentoBrFromPessoa();
  }

  /** Sincroniza {@link #dataNascimentoBr} a partir de {@link Pessoa#dataNascimento}. */
  private syncDataNascimentoBrFromPessoa(): void {
    const date = PessoaFormComponent.dataApiParaDateLocalMidnight(this.pessoa.dataNascimento as unknown);
    this.dataNascimentoBr = date ? PessoaFormComponent.formatarDataBr(date) : '';
  }

  /**
   * Ao digitar a data, atualiza o modelo quando a string está completa e válida.
   * `FRM-08` — a conversão de formato (dd/MM/yyyy na tela, `yyyy-MM-dd` na API) acontece
   * aqui, no formulário, nunca no service.
   */
  onDataNascimentoBrChange(): void {
    const parsed = PessoaFormComponent.parseDataBr(this.dataNascimentoBr);
    if (parsed) {
      this.pessoa.dataNascimento = PessoaFormComponent.dataIsoLocal(parsed);
    }
  }

  /** `yyyy-MM-dd` a partir dos componentes **locais** da data — nunca `toISOString()`. */
  private static dataIsoLocal(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private static formatarDataBr(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = date.getFullYear();
    return `${day}/${m}/${y}`;
  }

  /** DD/MM/AAAA → {@link Date} local (valida dia/mês/ano). */
  static parseDataBr(s: string): Date | undefined {
    const t = s?.trim();
    if (!t) return undefined;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(t);
    if (!m) return undefined;
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return undefined;
    }
    return d;
  }

  /** yyyy-MM-dd (Theos / ISO) → {@link Date} local. */
  static parseDataIso(iso: string): Date | undefined {
    const t = iso?.trim();
    if (!t) return undefined;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
    if (!m) return undefined;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return undefined;
    }
    return d;
  }

  /**
   * Converte data vinda da API para meia-noite **local** no dia civil correto.
   * Evita deslocar um dia ao usar {@code new Date('yyyy-MM-dd')} ou {@code Date} deserializado em UTC.
   */
  private static dataApiParaDateLocalMidnight(raw: unknown): Date | undefined {
    if (raw == null) return undefined;
    if (typeof raw === 'string') {
      const head = raw.trim().split('T')[0];
      return PessoaFormComponent.parseDataIso(head) ?? PessoaFormComponent.parseDataBr(raw.trim());
    }
    if (Array.isArray(raw) && raw.length >= 3) {
      const year = Number(raw[0]);
      const month = Number(raw[1]);
      const day = Number(raw[2]);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return undefined;
      const d = new Date(year, month - 1, day);
      if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return undefined;
      return d;
    }
    if (raw instanceof Date) {
      if (Number.isNaN(raw.getTime())) return undefined;
      const ly = raw.getFullYear();
      const lm = raw.getMonth();
      const ld = raw.getDate();
      const uy = raw.getUTCFullYear();
      const um = raw.getUTCMonth();
      const ud = raw.getUTCDate();
      if (ly !== uy || lm !== um || ld !== ud) {
        return new Date(uy, um, ud);
      }
      return new Date(ly, lm, ld);
    }
    if (typeof raw === 'number') {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return undefined;
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    }
    return undefined;
  }

  /** Rótulo legível no badge de sexo (M/F). */
  labelSexoBadge(valor: string): string {
    const u = valor.trim().toUpperCase();
    if (u === 'M') return 'Masculino';
    if (u === 'F') return 'Feminino';
    return valor;
  }

  aplicarValorTheos(campo: 'nome' | 'cpf' | 'sexo' | 'dataNascimento' | 'email'): void {
    const t = this.theosComparacao;
    if (!t?.disponivel) return;
    switch (campo) {
      case 'nome':
        if (t.theosNome) this.pessoa.nome = t.theosNome;
        break;
      case 'cpf':
        if (t.theosCpf) {
          this.pessoa.cpf = PessoaFormComponent.somenteDigitos(t.theosCpf);
        }
        break;
      case 'sexo': {
        if (!t.theosSexo) break;
        const s = t.theosSexo.trim().toUpperCase();
        if (s === 'M' || s === 'F') this.pessoa.sexo = s;
        break;
      }
      case 'dataNascimento':
        if (t.theosDataNascimento) {
          const d = PessoaFormComponent.parseDataIso(t.theosDataNascimento.trim());
          if (d) {
            this.pessoa.dataNascimento = PessoaFormComponent.dataIsoLocal(d);
            this.syncDataNascimentoBrFromPessoa();
          }
        }
        break;
      case 'email':
        if (t.theosEmail) this.pessoa.email = t.theosEmail;
        break;
    }
  }

  codigoTheosNumerico(codigo?: string | number | null): boolean {
    if (codigo == null) return false;
    return /^\d+$/.test(String(codigo).trim());
  }

  podeEnviarTheos(): boolean {
    return !!this.pessoa.id && this.codigoTheosNumerico(this.pessoa.codigoTheos);
  }

  private carregarComparacaoTheos(): void {
    const id = this.pessoa.id?.trim();
    if (!id || !this.codigoTheosNumerico(this.pessoa.codigoTheos)) {
      this.theosComparacao = null;
      this.zerarFlagsExibirBadgeTheos();
      return;
    }
    this.carregandoComparacaoTheos = true;
    this.pessoaService
      .buscarComparacaoTheos(id)
      .pipe(finalize(() => (this.carregandoComparacaoTheos = false)))
      .subscribe({
        next: (c) => {
          this.theosComparacao = c;
          this.sincronizarFlagsExibirBadgeTheosApartirComparacao();
        },
        error: () => {
          this.theosComparacao = null;
          this.zerarFlagsExibirBadgeTheos();
        },
      });
  }

  private zerarFlagsExibirBadgeTheos(): void {
    this.exibirBadgeTheosNome = false;
    this.exibirBadgeTheosCpf = false;
    this.exibirBadgeTheosSexo = false;
    this.exibirBadgeTheosData = false;
    this.exibirBadgeTheosEmail = false;
  }

  /** Atualiza visibilidade dos badges só com base na última comparação da API e no formulário atual. */
  private sincronizarFlagsExibirBadgeTheosApartirComparacao(): void {
    const t = this.theosComparacao;
    if (!t?.disponivel) {
      this.zerarFlagsExibirBadgeTheos();
      return;
    }
    this.exibirBadgeTheosNome = this.divergeNomeVsTheos(t);
    this.exibirBadgeTheosCpf = this.divergeCpfVsTheos(t);
    this.exibirBadgeTheosSexo = this.divergeSexoVsTheos(t);
    this.exibirBadgeTheosData = this.divergeDataNascimentoVsTheos(t);
    this.exibirBadgeTheosEmail = this.divergeEmailVsTheos(t);
  }

  private divergeNomeVsTheos(t: PessoaTheosComparacaoDto): boolean {
    return !!(t.theosNome && PessoaFormComponent.normNome(this.pessoa.nome) !== PessoaFormComponent.normNome(t.theosNome));
  }

  private divergeCpfVsTheos(t: PessoaTheosComparacaoDto): boolean {
    const b = t.theosCpf ? PessoaFormComponent.somenteDigitos(t.theosCpf) : '';
    if (!b) return false;
    const a = PessoaFormComponent.somenteDigitos(this.pessoa.cpf);
    return a !== b;
  }

  private divergeSexoVsTheos(t: PessoaTheosComparacaoDto): boolean {
    if (!t.theosSexo?.trim()) return false;
    const loc = (this.pessoa.sexo ?? '').trim().toUpperCase();
    const th = t.theosSexo.trim().toUpperCase();
    return loc !== th;
  }

  private divergeDataNascimentoVsTheos(t: PessoaTheosComparacaoDto): boolean {
    if (!t.theosDataNascimento?.trim()) return false;
    const loc = this.dataLocalIso();
    return loc !== t.theosDataNascimento.trim();
  }

  private divergeEmailVsTheos(t: PessoaTheosComparacaoDto): boolean {
    return !!(
      t.theosEmail &&
      PessoaFormComponent.normEmail(this.pessoa.email) !== PessoaFormComponent.normEmail(t.theosEmail)
    );
  }

  rotuloBadgeTheosNome(): string | null {
    if (!this.exibirBadgeTheosNome) return null;
    const n = this.theosComparacao?.theosNome?.trim();
    return n || null;
  }

  rotuloBadgeTheosCpf(): string | null {
    if (!this.exibirBadgeTheosCpf) return null;
    const c = this.theosComparacao?.theosCpf?.trim();
    return c || null;
  }

  rotuloBadgeTheosSexo(): string | null {
    if (!this.exibirBadgeTheosSexo) return null;
    const s = this.theosComparacao?.theosSexo?.trim();
    return s || null;
  }

  rotuloBadgeTheosDataBr(): string | null {
    if (!this.exibirBadgeTheosData) return null;
    const raw = this.theosComparacao?.theosDataNascimento?.trim();
    if (!raw) return null;
    const d = PessoaFormComponent.parseDataIso(raw);
    return d ? PessoaFormComponent.formatarDataBr(d) : raw;
  }

  rotuloBadgeTheosEmail(): string | null {
    if (!this.exibirBadgeTheosEmail) return null;
    const e = this.theosComparacao?.theosEmail?.trim();
    return e || null;
  }

  enviarParaTheos(): void {
    const id = this.pessoa.id?.trim();
    if (!id || !this.podeEnviarTheos()) return;
    const dto = this.toDto(this.pessoa);
    this.enviandoTheos = true;
    this.pessoaService
      .atualizar(id, dto)
      .pipe(
        switchMap((salva) => {
          if (salva) {
            this.pessoa.codigoTheos = salva.codigoTheos ?? this.pessoa.codigoTheos;
          }
          return this.pessoaService.enviarDadosParaTheos(id);
        }),
        finalize(() => (this.enviandoTheos = false)),
      )
      .subscribe({
        next: () => {
          this.toastr.success('Cadastro salvo e dados enviados ao Theos.', 'OK');
          if (this.codigoTheosNumerico(this.pessoa.codigoTheos)) {
            this.carregarComparacaoTheos();
          }
        },
        error: (err) => {
          const msg =
            mensagemDeErro(err, 'Não foi possível salvar ou enviar ao Theos.');
          this.toastr.danger(msg, 'Erro');
        },
      });
  }

  private static normNome(s?: string | null): string {
    return (s ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  private static somenteDigitos(s?: string | null): string {
    return (s ?? '').replace(/\D/g, '');
  }

  private static normEmail(s?: string | null): string {
    return (s ?? '').trim().toLowerCase();
  }

  private dataLocalIso(): string | null {
    const fromBr = PessoaFormComponent.parseDataBr(this.dataNascimentoBr);
    if (fromBr) {
      return PessoaFormComponent.dataIsoLocal(fromBr);
    }
    const date = PessoaFormComponent.dataApiParaDateLocalMidnight(this.pessoa.dataNascimento as unknown);
    return date ? PessoaFormComponent.dataIsoLocal(date) : null;
  }

  private dataNascimentoParaSalvar(pessoa: Pessoa): string | undefined {
    if (!this.dataNascimentoBr?.trim()) {
      return undefined;
    }
    const parsed = PessoaFormComponent.parseDataBr(this.dataNascimentoBr);
    if (parsed) {
      return PessoaFormComponent.dataIsoLocal(parsed);
    }
    const date = PessoaFormComponent.dataApiParaDateLocalMidnight(pessoa.dataNascimento as unknown);
    return date ? PessoaFormComponent.dataIsoLocal(date) : undefined;
  }

  private toDto(pessoa: Pessoa): PessoaDto {
    return {
      nome: pessoa.nome ?? '',
      cpf: pessoa.cpf ?? '',
      dataNascimento: this.dataNascimentoParaSalvar(pessoa),
      sexo: pessoa.sexo ?? '',
      email: pessoa.email ?? '',
      telefone: pessoa.telefone ?? '',
      batizado: pessoa.batizado ?? false,
      primeiraComunhao: pessoa.primeiraComunhao ?? false,
      necessidadeEspecial: pessoa.necessidadeEspecial ?? '',
      endereco: pessoa.endereco ?? '',
    };
  }

  temFotoRegistrada(): boolean {
    const id = this.pessoa.fotoDocumentoId;
    return this.pessoa.id != null && id != null && String(id).trim() !== '';
  }

  verFoto(): void {
    if (!this.temFotoRegistrada() || !this.pessoa.id) return;
    const docId = String(this.pessoa.fotoDocumentoId).trim();
    this.fotoPopupTitulo = this.pessoa.nome ?? 'Foto';
    const base = this.pessoaService.urlFotoPessoa(this.pessoa.id);
    this.fotoPopupUrl = `${base}?t=${Date.now()}&fotoDocumentoId=${encodeURIComponent(docId)}`;
    this.fotoPopupAberta = true;
  }

  fecharFotoPopup(): void {
    this.fotoPopupAberta = false;
    this.fotoPopupUrl = null;
    this.fotoPopupTitulo = '';
  }

  onFotoPopupErro(): void {
    this.toastr.danger('Não foi possível carregar a foto.', 'Erro');
    this.fecharFotoPopup();
  }

  confirmarApagarFoto(): void {
    const pid = this.pessoa.id?.trim();
    if (!pid || !this.temFotoRegistrada()) return;
    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Apagar foto',
          message: 'Remover a foto desta pessoa? Ela deixará de aparecer no sistema (cadastro e frequência).',
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, apagar foto',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.apagandoFoto = true;
        this.pessoaService
          .removerFoto(pid)
          .pipe(finalize(() => (this.apagandoFoto = false)))
          .subscribe({
            next: () => {
              this.pessoa.fotoDocumentoId = null;
              this.toastr.success('Foto removida.', 'OK');
            },
            error: (err) => {
              const msg =
                mensagemDeErro(err, 'Não foi possível apagar a foto.');
              this.toastr.danger(msg, 'Erro');
            },
          });
      });
  }

  salvar() {
    if (!this.formularioValido()) {
      return;
    }

    const dto = this.toDto(this.pessoa);

    if (this.pessoa.id) {
      this.pessoaService.atualizar(this.pessoa.id, dto).subscribe({
        next: (salva) => {
          if (salva) {
            this.pessoa.codigoTheos = salva.codigoTheos ?? this.pessoa.codigoTheos;
            if (this.codigoTheosNumerico(this.pessoa.codigoTheos)) {
              this.carregarComparacaoTheos();
            }
          }
          this.ref.close(true);
          this.dialogService.open(DialogAlertComponent, {
            context: {
              title: 'Sucesso',
              message: 'Pessoa atualizada com sucesso!',
            },
          });
        },
      });
    } else {
      this.pessoaService.salvar(dto).subscribe(() => {
        this.ref.close(true);
        this.dialogService.open(DialogAlertComponent, {
          context: {
            title: 'Sucesso',
            message: 'Pessoa cadastrada com sucesso!',
          },
        });
      });
    }
  }

  fechar() {
    this.ref.close(false);
  }
}
